import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { generateVerificationId } from '../utils/idGenerator';
import {
  DEFAULT_CATEGORIES,
  DEFAULT_PROGRAMS,
  DEFAULT_BATCHES,
  getLocalItem,
  setLocalItem,
  upsertCandidateToSupabase,
} from '../utils/supabaseClient';

const PlatformContext = createContext();

export function usePlatform() {
  return useContext(PlatformContext);
}

const STORAGE_KEY = 'certiflow_v3_data';
const SETTINGS_KEY = 'certiflow_v3_settings';
const PROGRAMS_KEY = 'certiflow_programs_v2';
const CATEGORIES_KEY = 'certiflow_categories_v2';
const BATCHES_KEY = 'certiflow_batches_v2';
const AUDIT_KEY = 'certiflow_audit_logs_v2';

const defaultSettings = {
  internship: {
    idPrefix: 'IH-FS', idYear: new Date().getFullYear().toString(), idLength: 4,
    organization: 'Innovation Hacks', authorizedPerson: 'Karthickkumar S',
    template: 'offer_v2', customTemplates: [], expiryDays: 365,
    emailSubject: 'Internship Offer Letter — Full Stack Development | Innovation Hacks',
    emailBody: `Dear {{candidate_name}},

Congratulations! 🎉 You have been selected for the Innovation Hacks Full Stack Development Internship.

Intern ID: {{id}}

📎 Download your Official QR-Verified Offer Letter attached to this email.

You can also verify your offer letter online at: {{verifyUrl}}

After downloading:

1. Convert the Offer Letter into an image.
2. Post your Offer Letter on LinkedIn and tag Innovation Hacks to showcase your global intern achievement!

🚀 After posting, stay active in the official communication channels to receive your task details and internship updates.

Welcome to the Innovation Hacks Family! ❤️

Build. Innovate. Impact. 🌍

Best Regards,
Karthickkumar S
Founder & CEO
Innovation Hacks`,
    publicSlug: 'innovation-hacks',
  },
  certificate: {
    idPrefix: 'IH-CERT', idYear: new Date().getFullYear().toString(), idLength: 4,
    organization: 'Innovation Hacks Academy', authorizedPerson: 'Karthickkumar S',
    template: 'course_v1', customTemplates: [], expiryDays: 730,
    emailSubject: '🏆 Certificate of Completion – {{candidate_name}} – {{id}}',
    emailBody: `Dear {{candidate_name}},

Congratulations on successfully completing your program at {{org}}!

Intern ID: {{id}}
Issue Date: {{issueDate}}

Please find your official Certificate attached to this email.

You can also verify your certificate online at: {{verifyUrl}}

Post your Certificate on LinkedIn and tag Innovation Hacks to showcase your global intern achievement!

Best Regards,
Karthickkumar S
Founder & CEO, Innovation Hacks`,
    publicSlug: 'innovation-hacks-academy',
  },
};

const defaultData = {
  internship: {
    candidates: [],
    applications: [],
    batches: [],
    cohorts: [],
    emailHistory: [],
    verificationLog: [],
  },
  certificate: {
    candidates: [],
    applications: [],
    batches: [],
    cohorts: [],
    emailHistory: [],
    verificationLog: [],
  },
  wallet: {
    badges: [],
    privacySettings: {},
  },
  notifications: [],
};

// Migrate old INT-YEAR- prefixed IDs, recover uploaded intern IDs, and fix roles
function migrateIds(stored) {
  try {
    const migrate = (candidates = []) =>
      candidates.map(c => {
        // Recover uploaded intern ID if it was stored in raw.__EMPTY, raw._internId, or raw['Intern ID']
        // but was replaced with an accidental IH-CERT- ID or old prefix
        const rawUploadedId = c.raw?._internId ||
          (c.raw?.__EMPTY && /^(IH|INT|CERT|FS|AI|DS|ML)[-_]/i.test(String(c.raw.__EMPTY).trim()) ? String(c.raw.__EMPTY).trim() : null) ||
          (c.raw?.['Intern ID'] ? String(c.raw['Intern ID']).trim() : null) ||
          (c.raw?.['Internship ID'] ? String(c.raw['Internship ID']).trim() : null);

        let finalGeneratedId = c.generatedId;
        if (rawUploadedId) {
          finalGeneratedId = rawUploadedId;
        } else if (c.generatedId && c.generatedId.startsWith('INT-')) {
          finalGeneratedId = 'IH-FS-' + c.generatedId.slice(4);
        }

        return {
          ...c,
          generatedId: finalGeneratedId,
          // Fix wrong default roles imported via EmailCenter or old AI presets
          role: (!c.role || c.role === 'Intern' || c.role.toLowerCase().includes('ai') || c.role.toLowerCase().includes('artificial'))
            ? 'Full Stack Development Intern'
            : c.role,
        };
      });
    if (stored?.internship?.candidates) {
      stored.internship.candidates = migrate(stored.internship.candidates);
    }
    if (stored?.certificate?.candidates) {
      stored.certificate.candidates = migrate(stored.certificate.candidates);
    }
  } catch {}
  return stored;
}

function loadFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : defaultData;
    return migrateIds(parsed);
  } catch { return defaultData; }
}

const NEW_INTERNSHIP_SUBJECT = 'Internship Offer Letter \u2014 Full Stack Development | Innovation Hacks';
const NEW_INTERNSHIP_BODY = `Dear {{candidate_name}},

Congratulations! 🎉 You have been selected for the Innovation Hacks Full Stack Development Internship.

Intern ID: {{id}}

📎 Download your Official QR-Verified Offer Letter attached to this email.

You can also verify your offer letter online at: {{verifyUrl}}

After downloading:

1. Convert the Offer Letter into an image.
2. Post your Offer Letter on LinkedIn and tag Innovation Hacks to showcase your global intern achievement!

🚀 After posting, stay active in the official communication channels to receive your task details and internship updates.

Welcome to the Innovation Hacks Family! ❤️

Build. Innovate. Impact. 🌍

Best Regards,
Karthickkumar S
Founder & CEO
Innovation Hacks`;

const NEW_CERTIFICATE_BODY = `Dear {{candidate_name}},

Congratulations on successfully completing your program at {{org}}!

Intern ID: {{id}}
Issue Date: {{issueDate}}

Please find your official Certificate attached to this email.

You can also verify your certificate online at: {{verifyUrl}}

Post your Certificate on LinkedIn and tag Innovation Hacks to showcase your global intern achievement!

Best Regards,
Karthickkumar S
Founder & CEO, Innovation Hacks`;

function migrateSettings(stored) {
  // Detect old templates containing "pleased to inform" or old date fields and replace
  if (stored?.internship) {
    const oldBody = stored.internship.emailBody || '';
    const oldSubject = stored.internship.emailSubject || '';
    if (
      oldBody.includes('pleased to inform') ||
      oldBody.includes('start_date') ||
      oldBody.includes('Internship Duration') ||
      oldSubject.includes('{{candidate_name}}') ||
      oldSubject.includes('{{internship_id}}') ||
      !oldBody.includes('Intern ID:') ||
      !oldBody.includes('global intern achievement') ||
      oldBody.includes('Karthickkumar\n')
    ) {
      stored.internship.emailBody = NEW_INTERNSHIP_BODY;
      stored.internship.emailSubject = NEW_INTERNSHIP_SUBJECT;
    }
    // Migrate old idPrefix (INT or IH) to IH-FS
    if (stored.internship.idPrefix === 'INT' || stored.internship.idPrefix === 'IH') {
      stored.internship.idPrefix = 'IH-FS';
    }
  }
  if (stored?.certificate) {
    const certBody = stored.certificate.emailBody || '';
    if (
      certBody.includes('Certificate ID:') ||
      !certBody.includes('LinkedIn') ||
      certBody.includes('Karthickkumar\n') ||
      certBody.includes('{{org}} Team')
    ) {
      stored.certificate.emailBody = NEW_CERTIFICATE_BODY;
    }
  }
  return stored;
}

function loadSettingsFromStorage() {
  try {
    const raw = localStorage.getItem(SETTINGS_KEY);
    const parsed = raw ? { ...defaultSettings, ...JSON.parse(raw) } : defaultSettings;
    return migrateSettings(parsed);
  } catch { return defaultSettings; }
}

export function PlatformProvider({ children }) {
  const [mode, setMode] = useState('internship');
  const [data, setData] = useState(() => loadFromStorage());
  const [settings, setSettings] = useState(() => loadSettingsFromStorage());
  const [studentSession, setStudentSession] = useState(null);

  // Relational Hierarchy State
  const [categories, setCategories] = useState(() => getLocalItem(CATEGORIES_KEY, DEFAULT_CATEGORIES));
  const [programs, setPrograms] = useState(() => getLocalItem(PROGRAMS_KEY, DEFAULT_PROGRAMS));
  const [batches, setBatches] = useState(() => getLocalItem(BATCHES_KEY, DEFAULT_BATCHES));
  const [auditLogs, setAuditLogs] = useState(() => getLocalItem(AUDIT_KEY, []));
  const [activeProgramId, setActiveProgramId] = useState('prog-ai-2026-08');

  // Sync to local storage
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  }, [data]);

  useEffect(() => {
    localStorage.setItem(SETTINGS_KEY, JSON.stringify(settings));
  }, [settings]);

  useEffect(() => {
    setLocalItem(CATEGORIES_KEY, categories);
  }, [categories]);

  useEffect(() => {
    setLocalItem(PROGRAMS_KEY, programs);
  }, [programs]);

  useEffect(() => {
    setLocalItem(BATCHES_KEY, batches);
  }, [batches]);

  useEffect(() => {
    setLocalItem(AUDIT_KEY, auditLogs);
  }, [auditLogs]);

  // ── AUDIT LOGGING ───────────────────────────────────────────────
  const logAuditAction = useCallback(({ action, entityType, entityId, programId, details = {} }) => {
    const log = {
      id: crypto.randomUUID(),
      action,
      entityType,
      entityId,
      programId: programId || activeProgramId,
      userEmail: 'admin@innovationhacks.in',
      details,
      createdAt: new Date().toISOString(),
    };
    setAuditLogs(prev => [log, ...prev]);
  }, [activeProgramId]);

  // ── CATEGORY MANAGEMENT ─────────────────────────────────────────
  const addCategory = useCallback((cat) => {
    const newCat = {
      id: `cat-${crypto.randomUUID().substring(0, 8)}`,
      name: cat.name,
      slug: cat.name.toLowerCase().replace(/[^a-z0-9]+/g, '-'),
      color: cat.color || '#0ea5e9',
      icon: cat.icon || 'FiCode',
      description: cat.description || '',
      isArchived: false,
      createdAt: new Date().toISOString(),
    };
    setCategories(prev => [newCat, ...prev]);
    logAuditAction({ action: 'Created Category', entityType: 'Category', entityId: newCat.id, details: { name: newCat.name } });
    return newCat;
  }, [logAuditAction]);

  const updateCategory = useCallback((id, updates) => {
    setCategories(prev => prev.map(c => c.id === id ? { ...c, ...updates, updatedAt: new Date().toISOString() } : c));
    logAuditAction({ action: 'Updated Category', entityType: 'Category', entityId: id, details: updates });
  }, [logAuditAction]);

  const deleteCategory = useCallback((id) => {
    setCategories(prev => prev.filter(c => c.id !== id));
    logAuditAction({ action: 'Deleted Category', entityType: 'Category', entityId: id });
  }, [logAuditAction]);

  // ── INTERNSHIP PROGRAM MANAGEMENT ───────────────────────────────
  const addProgram = useCallback((prog) => {
    const cat = categories.find(c => c.id === prog.categoryId);
    const newProg = {
      id: `prog-${crypto.randomUUID().substring(0, 8)}`,
      name: prog.name,
      code: prog.code || prog.name.split(' ').map(w => w[0]).join('').toUpperCase() + '-' + new Date().getFullYear(),
      categoryId: prog.categoryId,
      categoryName: cat?.name || 'General',
      domain: prog.domain || cat?.name || 'Technology',
      duration: prog.duration || '1 Month',
      startDate: prog.startDate || '2026-08-01',
      endDate: prog.endDate || '2026-08-31',
      applicationStartDate: prog.applicationStartDate || '2026-07-01',
      applicationEndDate: prog.applicationEndDate || '2026-07-25',
      mode: prog.mode || 'Online',
      location: prog.location || 'Remote / Virtual',
      mentor: prog.mentor || 'Innovation Hacks Lead',
      status: prog.status || 'Active',
      maximumSeats: Number(prog.maximumSeats) || 200,
      type: prog.type || 'Summer Internship',
      idPrefix: prog.idPrefix || 'IH-FS',
      year: Number(prog.year) || new Date(prog.startDate || Date.now()).getFullYear(),
      month: prog.month || new Date(prog.startDate || Date.now()).toLocaleString('default', { month: 'long' }),
      isArchived: false,
      description: prog.description || '',
      templateUrl: prog.templateUrl || '/template.png',
      createdAt: new Date().toISOString(),
    };

    // Also auto-create initial batch for the program
    const initialBatch = {
      id: `batch-${crypto.randomUUID().substring(0, 8)}`,
      programId: newProg.id,
      batchName: `${newProg.month} ${newProg.year} Batch`,
      batchCode: `${newProg.idPrefix}-${newProg.month.substring(0, 3).toUpperCase()}-${String(newProg.year).slice(-2)}`,
      startDate: newProg.startDate,
      endDate: newProg.endDate,
      capacity: newProg.maximumSeats,
      status: newProg.status,
      year: newProg.year,
      month: newProg.month,
      isArchived: false,
      createdAt: new Date().toISOString(),
    };

    setPrograms(prev => [newProg, ...prev]);
    setBatches(prev => [initialBatch, ...prev]);
    setActiveProgramId(newProg.id);

    logAuditAction({ action: 'Created Internship Program', entityType: 'Program', entityId: newProg.id, programId: newProg.id, details: { name: newProg.name } });
    return newProg;
  }, [categories, logAuditAction]);

  const updateProgram = useCallback((id, updates) => {
    setPrograms(prev => prev.map(p => p.id === id ? { ...p, ...updates, updatedAt: new Date().toISOString() } : p));
    logAuditAction({ action: 'Updated Program', entityType: 'Program', entityId: id, programId: id, details: updates });
  }, [logAuditAction]);

  const archiveProgram = useCallback((id) => {
    setPrograms(prev => prev.map(p => p.id === id ? { ...p, status: 'Archived', isArchived: true, archivedAt: new Date().toISOString() } : p));
    setBatches(prev => prev.map(b => b.programId === id ? { ...b, isArchived: true } : b));
    logAuditAction({ action: 'Archived Program', entityType: 'Program', entityId: id, programId: id });
  }, [logAuditAction]);

  const reopenProgram = useCallback((id) => {
    setPrograms(prev => prev.map(p => p.id === id ? { ...p, status: 'Active', isArchived: false, archivedAt: null } : p));
    setBatches(prev => prev.map(b => b.programId === id ? { ...b, isArchived: false } : b));
    logAuditAction({ action: 'Reopened Program', entityType: 'Program', entityId: id, programId: id });
  }, [logAuditAction]);

  const duplicateProgram = useCallback((sourceProgramId, newBatchMonth = 'Next Month', newYear = 2026) => {
    const src = programs.find(p => p.id === sourceProgramId);
    if (!src) return null;

    const newProg = {
      ...src,
      id: `prog-${crypto.randomUUID().substring(0, 8)}`,
      name: `${src.name} (${newBatchMonth} ${newYear})`,
      month: newBatchMonth,
      year: Number(newYear),
      status: 'Upcoming',
      isArchived: false,
      createdAt: new Date().toISOString(),
    };

    const newBatch = {
      id: `batch-${crypto.randomUUID().substring(0, 8)}`,
      programId: newProg.id,
      batchName: `${newBatchMonth} ${newYear} Batch`,
      batchCode: `${src.idPrefix}-${newBatchMonth.substring(0, 3).toUpperCase()}-${String(newYear).slice(-2)}`,
      startDate: newProg.startDate,
      endDate: newProg.endDate,
      capacity: newProg.maximumSeats,
      status: 'Upcoming',
      year: Number(newYear),
      month: newBatchMonth,
      isArchived: false,
      createdAt: new Date().toISOString(),
    };

    setPrograms(prev => [newProg, ...prev]);
    setBatches(prev => [newBatch, ...prev]);
    setActiveProgramId(newProg.id);

    logAuditAction({ action: 'Duplicated Program', entityType: 'Program', entityId: newProg.id, programId: newProg.id, details: { sourceId: sourceProgramId } });
    return newProg;
  }, [programs, logAuditAction]);

  // ── BATCH MANAGEMENT ────────────────────────────────────────────
  const addBatch = useCallback((batch) => {
    const newBatch = {
      id: `batch-${crypto.randomUUID().substring(0, 8)}`,
      programId: batch.programId || activeProgramId,
      batchName: batch.batchName,
      batchCode: batch.batchCode || `BATCH-${Date.now().toString().slice(-4)}`,
      startDate: batch.startDate || '2026-09-01',
      endDate: batch.endDate || '2026-09-30',
      capacity: Number(batch.capacity) || 150,
      status: batch.status || 'Active',
      year: Number(batch.year) || new Date(batch.startDate || Date.now()).getFullYear(),
      month: batch.month || new Date(batch.startDate || Date.now()).toLocaleString('default', { month: 'long' }),
      isArchived: false,
      createdAt: new Date().toISOString(),
    };
    setBatches(prev => [newBatch, ...prev]);
    logAuditAction({ action: 'Created Batch', entityType: 'Batch', entityId: newBatch.id, programId: newBatch.programId, details: { batchName: newBatch.batchName } });
    return newBatch;
  }, [activeProgramId, logAuditAction]);

  const updateBatch = useCallback((id, updates) => {
    setBatches(prev => prev.map(b => b.id === id ? { ...b, ...updates, updatedAt: new Date().toISOString() } : b));
    logAuditAction({ action: 'Updated Batch', entityType: 'Batch', entityId: id, details: updates });
  }, [logAuditAction]);

  const deleteBatch = useCallback((id) => {
    setBatches(prev => prev.filter(b => b.id !== id));
    logAuditAction({ action: 'Deleted Batch', entityType: 'Batch', entityId: id });
  }, [logAuditAction]);

  // ── CANDIDATE CRUD ───────────────────────────────────────────────
  const addCandidates = useCallback((newCandidates, targetProgramId, targetBatchId) => {
    const pId = targetProgramId || activeProgramId;
    const bId = targetBatchId || batches.find(b => b.programId === pId)?.id;

    const enriched = newCandidates.map(c => ({
      ...c,
      programId: c.programId || pId,
      batchId: c.batchId || bId,
    }));

    setData(prev => ({
      ...prev,
      [mode]: { ...prev[mode], candidates: [...(prev[mode].candidates || []), ...enriched] }
    }));

    // Sync to Supabase (fire-and-forget)
    enriched.forEach(c => upsertCandidateToSupabase(c));

    logAuditAction({ action: 'Imported Candidates', entityType: 'Candidate', programId: pId, details: { count: enriched.length } });
  }, [mode, activeProgramId, batches, logAuditAction]);

  const updateCandidate = useCallback((id, updates) => {
    setData(prev => {
      const updatedCandidates = (prev[mode].candidates || []).map(c =>
        c.id === id ? { ...c, ...updates } : c
      );
      // Sync to Supabase when a verifiable ID is set/changed, cert status changes,
      // or docStatus transitions to 'generated' (offer letter / certificate produced).
      // Only syncing on docStatus === 'generated' avoids unnecessary writes for
      // intermediate status changes (e.g. 'pending' → 'pending').
      const shouldSync =
        updates.generatedId      ||
        updates.verificationId   ||
        updates.certStatus       ||
        updates.docStatus === 'generated';
      if (shouldSync) {
        const updated = updatedCandidates.find(c => c.id === id);
        if (updated) upsertCandidateToSupabase(updated);
      }
      return {
        ...prev,
        [mode]: { ...prev[mode], candidates: updatedCandidates }
      };
    });
  }, [mode]);

  const deleteCandidate = useCallback((id) => {
    if (!id) return;
    setData(prev => {
      const currentList = prev[mode]?.candidates || [];
      const updatedList = currentList.filter(c => 
        c.id !== id &&
        String(c.id) !== String(id) &&
        c.generatedId !== id &&
        c.verificationId !== id
      );
      return {
        ...prev,
        [mode]: {
          ...prev[mode],
          candidates: updatedList
        }
      };
    });
    logAuditAction({ action: 'Deleted Candidate', entityType: 'Candidate', entityId: id });
  }, [mode, logAuditAction]);

  const deleteAllCandidates = useCallback((targetProgramId) => {
    setData(prev => {
      const modeData = prev[mode] || {};
      const updatedCandidates = targetProgramId
        ? (modeData.candidates || []).filter(c => c.programId !== targetProgramId)
        : [];
      return {
        ...prev,
        [mode]: {
          ...modeData,
          candidates: updatedCandidates
        }
      };
    });
    logAuditAction({ action: 'Deleted All Candidates', entityType: 'Candidate', programId: targetProgramId });
  }, [mode, logAuditAction]);

  // ── Certificate Lifecycle ────────────────────────────────────────
  const revokeCredential = useCallback((id, reason = '') => {
    setData(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        candidates: (prev[mode].candidates || []).map(c =>
          c.id === id ? { ...c, certStatus: 'revoked', revokedAt: new Date().toISOString(), revokeReason: reason, revokedBy: 'Admin' } : c
        )
      }
    }));
    logAuditAction({ action: 'Revoked Credential', entityType: 'Certificate', entityId: id, details: { reason } });
  }, [mode, logAuditAction]);

  const renewCredential = useCallback((id, extraDays) => {
    setData(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        candidates: (prev[mode].candidates || []).map(c => {
          if (c.id !== id) return c;
          return { ...c, certStatus: 'renewed' };
        })
      }
    }));
  }, [mode]);

  const reissueCredential = useCallback((id) => {
    setData(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        candidates: (prev[mode].candidates || []).map(c => {
          if (c.id !== id) return c;
          const hist = c.versionHistory || [];
          const newVerifId = generateVerificationId(mode === 'internship' ? 'INT' : 'CERT');
          return {
            ...c, certStatus: 'reissued', verificationId: newVerifId, docStatus: 'generated',
            reissuedAt: new Date().toISOString(),
            versionHistory: [...hist, { action: 'reissued', date: new Date().toISOString(), oldVerifId: c.verificationId }]
          };
        })
      }
    }));
    logAuditAction({ action: 'Reissued Credential', entityType: 'Certificate', entityId: id });
  }, [mode, logAuditAction]);

  const archiveCredential = useCallback((id) => {
    setData(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        candidates: (prev[mode].candidates || []).map(c =>
          c.id === id ? { ...c, certStatus: 'archived', archivedAt: new Date().toISOString() } : c
        )
      }
    }));
  }, [mode]);

  const transferOwnership = useCallback((id, newOwnerEmail) => {
    setData(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        candidates: (prev[mode].candidates || []).map(c =>
          c.id === id ? { ...c, ownerEmail: newOwnerEmail, transferredAt: new Date().toISOString() } : c
        )
      }
    }));
  }, [mode]);

  // ── Issue Completion Certificate from an existing Offer Letter ────
  // Creates a new candidate record in the 'certificate' data slice with a
  // fresh credential ID and QR, while leaving the internship record intact.
  const issueCertificateFromOfferLetter = useCallback((internCandidate) => {
    const certSettings = settings.certificate || {};
    const prefix = certSettings.idPrefix || 'IH-CERT';
    const year   = certSettings.idYear   || new Date().getFullYear();
    const len    = certSettings.idLength  || 4;
    const certVerifId  = generateVerificationId('CERT');
    const candidateId  = crypto.randomUUID();
    const now = new Date().toISOString();

    // Build the certificate candidate object inside setData so we always
    // use the latest certificate list for auto-numbering.
    let finalCert = null;

    setData(prev => {
      const existingCerts = prev.certificate?.candidates || [];
      const seq = existingCerts.length + 1;
      finalCert = {
        id:          candidateId,
        name:        internCandidate.name,
        email:       internCandidate.email,
        phone:       internCandidate.phone       || null,
        college:     internCandidate.college     || null,
        department:  internCandidate.department  || null,
        role:        internCandidate.role || internCandidate.domain || 'Full Stack Development Intern',
        duration:    internCandidate.duration    || '1 Month',
        startDate:   internCandidate.startDate   || '2026-08-22',
        endDate:     internCandidate.endDate     || '2026-09-22',
        generatedId:           `${prefix}-${year}-${String(seq).padStart(len, '0')}`,
        verificationId:        certVerifId,
        sourceOfferLetterId:   internCandidate.generatedId        || null,
        sourceOfferLetterVerifyId: internCandidate.verificationId || null,
        docStatus:       'generated',
        certStatus:      'active',
        candidateStatus: 'Selected',
        documentType:    'completion_certificate',
        createdAt:       now,
        issuedAt:        now,
        raw: {
          sourceOfferLetterId:      internCandidate.generatedId,
          sourceOfferLetterVerifyId: internCandidate.verificationId,
        },
      };
      return {
        ...prev,
        certificate: {
          ...(prev.certificate || {}),
          candidates: [...existingCerts, finalCert],
        },
      };
    });

    // Fire-and-forget Supabase sync after setData has committed
    setTimeout(() => { if (finalCert) upsertCandidateToSupabase(finalCert); }, 50);

    logAuditAction({
      action: 'Issued Completion Certificate',
      entityType: 'Certificate',
      entityId: candidateId,
      details: {
        sourceOfferLetterId: internCandidate.generatedId,
        recipientName: internCandidate.name,
      },
    });

    return certVerifId; // caller can use this to confirm generation
  }, [settings, logAuditAction]);

  // ── Email ────────────────────────────────────────────────────────
  const addEmailToHistory = useCallback((emailRecord) => {
    setData(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        emailHistory: [emailRecord, ...(prev[mode].emailHistory || [])]
      }
    }));
  }, [mode]);

  const updateEmailRecord = useCallback((emailId, updates) => {
    setData(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        emailHistory: (prev[mode].emailHistory || []).map(e =>
          e.id === emailId ? { ...e, ...updates } : e
        )
      }
    }));
  }, [mode]);

  // ── Settings & Per-Type Custom Templates ─────────────────────────
  const updateSettings = useCallback((newSettings) => {
    setSettings(prev => ({
      ...prev,
      [mode]: { ...prev[mode], ...newSettings }
    }));
  }, [mode]);

  const uploadCustomTemplateForType = useCallback((typeId, file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target.result;
      const newTemplate = {
        id: crypto.randomUUID(),
        typeId,
        name: file.name,
        url: dataUrl,
        type: file.type.includes('image') ? 'image' : 'pdf',
        uploadedAt: new Date().toISOString(),
      };

      setSettings(prev => {
        const modeSettings = prev[mode] || {};
        const prevMap = modeSettings.customTemplatesMap || {};
        const newMap = { ...prevMap, [typeId]: newTemplate };

        return {
          ...prev,
          [mode]: {
            ...modeSettings,
            customTemplatesMap: newMap,
            template: typeId, // Automatically activate the uploaded template type
          }
        };
      });
    };
    reader.readAsDataURL(file);
  }, [mode]);

  const resetTemplateForType = useCallback((typeId) => {
    setSettings(prev => {
      const modeSettings = prev[mode] || {};
      const prevMap = modeSettings.customTemplatesMap || {};
      const newMap = { ...prevMap };
      delete newMap[typeId];

      return {
        ...prev,
        [mode]: {
          ...modeSettings,
          customTemplatesMap: newMap,
        }
      };
    });
  }, [mode]);

  const uploadCustomTemplate = useCallback((file) => {
    uploadCustomTemplateForType('offer_v2', file);
  }, [uploadCustomTemplateForType]);

  const deleteCustomTemplate = useCallback((id) => {
    setSettings(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        customTemplates: (prev[mode].customTemplates || []).filter(t => t.id !== id),
        template: prev[mode].template === id ? 'offer_v2' : prev[mode].template,
      }
    }));
  }, [mode]);

  const logVerification = useCallback((verifRecord) => {
    setData(prev => ({
      ...prev,
      [mode]: {
        ...prev[mode],
        verificationLog: [verifRecord, ...(prev[mode].verificationLog || [])]
      }
    }));
  }, [mode]);

  const modeData = data[mode] || {};

  const value = {
    mode, setMode,
    // Relational Hierarchy
    categories, addCategory, updateCategory, deleteCategory,
    programs, addProgram, updateProgram, archiveProgram, reopenProgram, duplicateProgram,
    batches, addBatch, updateBatch, deleteBatch,
    activeProgramId, setActiveProgramId,
    auditLogs, logAuditAction,
    // Current mode data
    candidates: modeData.candidates || [],
    applications: data.internship?.applications || [],
    emailHistory: modeData.emailHistory || [],
    verificationLog: modeData.verificationLog || [],
    operations: data.internship?.operations || {},
    settings: settings[mode],
    allSettings: settings,
    // Global & notifications
    notifications: data.notifications || [],
    studentSession, setStudentSession,
    // Internship candidates (available in all modes — used by Certificate Registry)
    internshipCandidates: data.internship?.candidates || [],
    // Candidate actions
    addCandidates, updateCandidate, deleteCandidate, deleteAllCandidates,
    // Certificate lifecycle
    revokeCredential, renewCredential, reissueCredential, archiveCredential, transferOwnership,
    issueCertificateFromOfferLetter,
    // Email
    addEmailToHistory, updateEmailRecord,
    // Verification
    logVerification,
    // Settings & Custom Templates
    updateSettings, uploadCustomTemplate, deleteCustomTemplate,
    uploadCustomTemplateForType, resetTemplateForType,
    customTemplatesMap: settings[mode]?.customTemplatesMap || {},
    // All raw data
    allData: data,
  };

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
}